import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { setComposerUnsavedWork } from "../lib/composer-draft";
import { WorkspaceWindowIdContext } from "../lib/workspace-local-state";

type PendingWork = { app: "editor" | "messages"; windowId: string | null; pending: boolean };
const Context = createContext<Map<string, PendingWork> | null>(null);

/** A close targets one window; entries from other windows keep their own warning. */
const belongsTo = (item: PendingWork, closing: string) =>
  item.windowId === null ? item.app === closing : item.windowId === closing;

/** Lives outside Activity: hiding a view must not clear its unsaved-work warning. */
export function WorkspaceLifetime({ children }: { children: ReactNode }) {
  const [work] = useState(() => new Map<string, PendingWork>());
  const { t } = useLingui();
  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if ([...work.values()].some((item) => item.pending)) event.preventDefault();
    };
    const closing = (event: Event) => {
      const target = (event as CustomEvent).detail;
      if (
        [...work.values()].some((item) => belongsTo(item, target) && item.pending) &&
        !window.confirm(t`Discard unsaved changes?`)
      )
        event.preventDefault();
    };
    const closed = (event: Event) => {
      const target = (event as CustomEvent).detail;
      for (const [key, item] of work) if (belongsTo(item, target)) work.delete(key);
      // The composer flag is shared, so it only clears once no conversation still holds work.
      if (![...work.values()].some((item) => item.app === "messages" && item.pending))
        setComposerUnsavedWork(false);
    };
    window.addEventListener("beforeunload", beforeUnload);
    window.addEventListener("workspace-close", closing);
    window.addEventListener("workspace-closed", closed);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      window.removeEventListener("workspace-close", closing);
      window.removeEventListener("workspace-closed", closed);
    };
  }, [work, t]);
  useEffect(
    () => () => {
      work.clear();
      setComposerUnsavedWork(false);
    },
    [work],
  );
  return <Context.Provider value={work}>{children}</Context.Provider>;
}

export function useWorkspacePendingWork(key: string, app: PendingWork["app"], pending: boolean) {
  const work = useContext(Context);
  const windowId = useContext(WorkspaceWindowIdContext);
  useEffect(() => {
    work?.set(windowId ? `${windowId}:${key}` : key, { app, windowId, pending });
    // Cleanup is owned by the workspace: Activity also runs effect cleanup when hiding a view.
  }, [work, key, app, windowId, pending]);
  return work !== null;
}
